import { lazy, Suspense, useEffect, useState } from "react";

import type { TeamMember } from "@ravnar/api-contract";

import { useSession } from "../../api/session";
import { PARTY_KIND_BLURBS, PARTY_KIND_DOT_CLASS, PARTY_KINDS, type PartyKind } from "../../domain/directory";
import { assignableRoles } from "../../domain/settings";
import { PlusIcon } from "../icons";
import { CommandDialog, type Command } from "../ui/command-dialog";

// The drawers the commands open are loaded the first time the menu opens, not with the shell every page loads: the
// person's and the parties' forms bring the note editor, the largest thing the app has, which only their pages loaded.
const AddPartyDrawer = lazy(async () => {
	const { AddPartyDrawer: drawer } = await import("../../routes/directory/-components/add-party-drawer");
	return { default: drawer };
});
const UploadDrawer = lazy(async () => {
	const { UploadDrawer: drawer } = await import("../../routes/intake/-components/upload-drawer");
	return { default: drawer };
});
const AddMemberDrawer = lazy(async () => {
	const { AddMemberDrawer: drawer } = await import("../../routes/settings/-components/add-member-drawer");
	return { default: drawer };
});

/** What the New menu adds: a party of each kind, documents into Intake, and a member of the team. */
type NewCommandId = PartyKind | "documents" | "member";

interface NewCommand extends Command<NewCommandId> {
	/** Whether the member signed in may do it. The API refuses anyone else, so the menu offers it to nobody else. */
	readonly isFor: (member: TeamMember) => boolean;
}

/** Whether a member may add vendors, customers and "other" parties, which the Directory owns outright. */
function editsDirectory(member: TeamMember): boolean {
	return member.access.directory === "editor";
}

/**
 * Everything the New menu can add, in the order it lists them; each command opens its own drawer over the page. A new
 * kind of party needs a command here as well as its form in the Add party drawer's `PARTY_FORMS`.
 */
const NEW_COMMANDS: readonly NewCommand[] = [
	{
		id: "person",
		label: "Add person",
		description: PARTY_KIND_BLURBS.person,
		group: "Directory",
		hint: "People & HR",
		dotClassName: PARTY_KIND_DOT_CLASS.person,
		keywords: ["employee", "contractor", "hire", "people"],
		// A person's record is People & HR's, which is whose editors the API lets add one, whichever drawer asks.
		isFor: member => member.access.people === "editor",
	},
	{
		id: "vendor",
		label: "Add vendor",
		description: PARTY_KIND_BLURBS.vendor,
		group: "Directory",
		hint: "Directory",
		dotClassName: PARTY_KIND_DOT_CLASS.vendor,
		keywords: ["supplier"],
		isFor: editsDirectory,
	},
	{
		id: "customer",
		label: "Add customer",
		description: PARTY_KIND_BLURBS.customer,
		group: "Directory",
		hint: "Directory",
		dotClassName: PARTY_KIND_DOT_CLASS.customer,
		keywords: ["client", "prospect"],
		isFor: editsDirectory,
	},
	{
		id: "other",
		label: "Add other party",
		description: PARTY_KIND_BLURBS.other,
		group: "Directory",
		hint: "Directory",
		dotClassName: PARTY_KIND_DOT_CLASS.other,
		keywords: ["bank", "insurer", "investor"],
		isFor: editsDirectory,
	},
	{
		id: "documents",
		label: "Upload documents",
		description: "Invoices, contracts, receipts — Document Filing reads each one.",
		group: "Documents",
		hint: "Intake",
		dotClassName: "bg-ops",
		keywords: ["file", "invoice", "receipt", "contract", "pdf", "scan"],
		// Every member may upload: the API asks for a session and nothing more.
		isFor: () => true,
	},
	{
		id: "member",
		label: "Add team member",
		description: "Someone who signs in, and what they can see in each section.",
		group: "Organization",
		hint: "Team & Access",
		dotClassName: "bg-gold",
		keywords: ["invite", "user", "colleague", "access"],
		// Owners and admins add members; the roles they may give say so, as they do on Team & Access.
		isFor: member => assignableRoles(member.role).length > 0,
	},
];

/** Whether a key press is the New menu's shortcut: ⌘K on a Mac, Ctrl+K elsewhere — either is taken everywhere. */
function isNewShortcut(event: KeyboardEvent): boolean {
	return event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey;
}

/**
 * The rail's New button and what it opens: a modal of every command this member may run to add something — a party,
 * documents, a team member — which ⌘K (Ctrl+K) opens from anywhere and closes again. Choosing one closes the modal and
 * opens that command's own drawer over the page, which stays where it was: cancelling leaves the member on it, and
 * saving goes wherever that drawer goes — a new party's page, or nowhere at all.
 */
export function NewMenu() {
	const { member } = useSession();
	const [isChoosing, setIsChoosing] = useState(false);
	/**
	 * Whether the menu has opened yet. The drawers of the commands this member is offered are loaded then, and stay
	 * mounted from then on, closed, so that each one closes itself and hands focus back to where it came from.
	 */
	const [hasOpened, setHasOpened] = useState(false);
	/** The command whose drawer is open, or on its way while its code loads, if one is. */
	const [running, setRunning] = useState<NewCommandId | undefined>(undefined);
	const commands = NEW_COMMANDS.filter(command => command.isFor(member));
	/**
	 * Whether this member is offered a command, and so needs its drawer: a member offered none of the parties never loads
	 * the note editor their forms bring.
	 */
	const offers = (id: NewCommandId) => commands.some(command => command.id === id);
	const partyKind = PARTY_KINDS.find(kind => kind === running);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (!isNewShortcut(event) || event.repeat) {
				return;
			}
			// Over a drawer or a confirm dialog the shortcut does nothing: a second modal would open on top of the first. Nor
			// while a chosen command's drawer is still loading, which opens the moment it has.
			if (!isChoosing && (running !== undefined || document.querySelector("dialog[open]") !== null)) {
				return;
			}
			event.preventDefault();
			setIsChoosing(!isChoosing);
			setHasOpened(true);
		};
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [isChoosing, running]);

	/**
	 * Closes the drawer a command opened, if it is still the one open. Each drawer calls it on its own close too, and an
	 * upload or a save that finishes after its drawer was closed calls it late — by when the member may have opened
	 * another command's drawer, which must stay open.
	 */
	const finish = (id: NewCommandId) => {
		setRunning(current => (current === id ? undefined : current));
	};
	const finishParty = () => {
		if (partyKind !== undefined) {
			finish(partyKind);
		}
	};

	return (
		<>
			<button
				type="button"
				aria-haspopup="dialog"
				aria-keyshortcuts="Meta+K Control+K"
				onClick={() => {
					// A browser makes the button inert under a command's drawer, but not while that drawer is still loading.
					if (running !== undefined) {
						return;
					}
					setIsChoosing(true);
					setHasOpened(true);
				}}
				className="mx-3 mb-2.5 flex w-[calc(100%-24px)] press cursor-pointer items-center gap-2 rounded-field border border-white/[0.12] bg-white/[0.05] px-[11px] py-2 text-ctl-lg font-medium text-[#FAF8F3]/90 shadow-[0_3px_12px_rgba(18,133,122,.26)] transition-all hover:border-white/20 hover:bg-white/[0.10]"
			>
				<PlusIcon className="h-[13px] w-[13px] fill-none stroke-current stroke-[2]" strokeLinecap="round" />
				New
				<kbd aria-hidden="true" className="ml-auto text-micro text-white/40">
					⌘K
				</kbd>
			</button>
			{/* The modal comes before the drawers: choosing closes it before the chosen drawer opens, and the browser hands
			    focus back to where it was before the drawer takes it. */}
			<CommandDialog
				open={isChoosing}
				title="New"
				placeholder="What do you want to add?"
				commands={commands}
				onChoose={id => {
					setIsChoosing(false);
					setRunning(id);
				}}
				onClose={() => {
					setIsChoosing(false);
				}}
			/>
			{hasOpened ? (
				<Suspense fallback={null}>
					{PARTY_KINDS.some(offers) ? (
						<AddPartyDrawer
							open={partyKind !== undefined}
							kind={partyKind}
							onClose={finishParty}
							onSaved={finishParty}
						/>
					) : null}
					{offers("documents") ? (
						<UploadDrawer
							open={running === "documents"}
							onClose={() => {
								finish("documents");
							}}
						/>
					) : null}
					{offers("member") ? (
						<AddMemberDrawer
							open={running === "member"}
							onClose={() => {
								finish("member");
							}}
						/>
					) : null}
				</Suspense>
			) : null}
		</>
	);
}
